import { useState } from 'react';
import {
  faChevronDown,
  faChevronUp,
  faCircleInfo,
  faMagnifyingGlass,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Typography } from '@design-system/components/typography/typography';
import { IconButton } from '@design-system/components/button/iconButton';
import { Toggle } from '@client/components/common/inputs/Toggle';
import { useCollectionSwapConfigQuery } from 'generated/graphql';
import {
  ExerciseSwapConfig,
  MAX_SWAP_PROPOSALS,
  SwapSet,
  defaultSwapConfig,
  exerciseNameKey,
  proposalFromCatalog,
} from '../model/swaps.domain';
import {
  SwapCatalogHit,
  useSwapCatalogSearch,
} from '../model/useCatalogSearch';
import { useIsCreatorApprovedSwapsEnabled } from '../model/useIsCreatorApprovedSwapsEnabled';
import { CatalogSearch } from './CatalogSearch';
import { ModificationsList } from './ModificationsList';
import { ExerciseSettingsModal } from './ExerciseSettingsModal';
import * as styles from './CreatorApprovedSwaps.css';

type CreatorApprovedSwapsFormProps = {
  value: ExerciseSwapConfig | undefined;
  onChange: (value: ExerciseSwapConfig) => void;
  exerciseName: string;
  exerciseSets: SwapSet[];
  influencerId: number;
  programId?: number;
  workoutSwapsOff?: boolean;
  unavailable?: boolean;
  sharedWithEarlier?: boolean;
  disabled?: boolean;
};

export const CreatorApprovedSwapsForm = (p: CreatorApprovedSwapsFormProps) => {
  const enabled = useIsCreatorApprovedSwapsEnabled(p.influencerId);
  const { data } = useCollectionSwapConfigQuery({
    variables: { collectionId: p.programId ?? 0 },
    skip: !enabled || p.programId === undefined,
  });
  if (!enabled) return null;

  // Program/workout switches only block editing here - the stored exercise
  // config is untouched and still saves as-is.
  const programSwapsOff = data?.collection.swapConfig?.swapsEnabled === false;
  const blockedNote = programSwapsOff
    ? 'Exercise modifications are off for this program. Turn them on at the program level to enable this.'
    : p.workoutSwapsOff
    ? 'Exercise modifications are off for this workout. Turn them on at the workout level to enable this.'
    : undefined;

  return (
    <>
      <hr className={styles.cardDivider} />
      <div className={styles.section}>
        {blockedNote !== undefined ? (
          <BlockedHeader note={blockedNote} />
        ) : p.unavailable ? (
          <NoteHeader note="Exercise modifications are temporarily unavailable. Reload the page to try again." />
        ) : p.sharedWithEarlier ? (
          <NoteHeader
            note={`This exercise already has modifications set on the first '${p.exerciseName}' in this workout, so you don't need to set them again here. Want to change them? Modify the original one and it'll update here as well.`}
          />
        ) : (
          <SectionBody {...p} />
        )}
      </div>
    </>
  );
};

const Title = ({ count }: { count?: number }) => (
  <Typography variant="body" size="large">
    Exercise modifications{count !== undefined ? ` (${count})` : ''}
  </Typography>
);

// Static card: switch shown off and inert, no chevron, no body. The note
// explains which level turned modifications off.
const BlockedHeader = ({ note }: { note: string }) => (
  <div className={styles.header}>
    <div className={styles.headerText}>
      <Title />
      <Typography variant="body" size="medium" color="N-80">
        {note}
      </Typography>
    </div>
    <div className={styles.headerControls}>
      <div data-testid="swap-section-toggle">
        <Toggle value={false} variant="primary" disabled />
      </div>
    </div>
  </div>
);

const NoteHeader = ({ note }: { note: string }) => (
  <div className={styles.header}>
    <div className={styles.headerText}>
      <Title />
      <span className={styles.unavailableNote}>{note}</span>
    </div>
  </div>
);

const SectionBody = (p: CreatorApprovedSwapsFormProps) => {
  const [open, setOpen] = useState(false);
  const [editedNameKey, setEditedNameKey] = useState<string | null>(null);
  const config = p.value ?? defaultSwapConfig();
  const editedProposal = config.proposals.find(
    (proposal) => exerciseNameKey(proposal.name) === editedNameKey,
  );
  const search = useSwapCatalogSearch({
    influencerId: p.influencerId,
    exerciseName: p.exerciseName,
    proposals: config.proposals,
  });
  const update = (next: Partial<ExerciseSwapConfig>) =>
    p.onChange({ ...config, ...next });
  const addProposal = (hit: SwapCatalogHit) =>
    update({
      proposals: [
        ...config.proposals,
        proposalFromCatalog(hit.exercise, p.exerciseSets),
      ],
    });

  return (
    <>
      <div className={styles.header}>
        <div className={styles.headerText}>
          <Title
            count={
              !open && config.swapsEnabled ? config.proposals.length : undefined
            }
          />
          <Typography variant="body" size="medium" color="N-80">
            Allow AI-suggested swaps for this exercise. Program your own below
            to use those instead.
          </Typography>
        </div>
        <div className={styles.headerControls}>
          <div data-testid="swap-section-toggle">
            <Toggle
              value={config.swapsEnabled}
              variant="primary"
              disabled={p.disabled}
              // Toggle's onClick fires even when styled disabled - withhold
              // the handler to actually block the interaction.
              onChange={
                p.disabled
                  ? undefined
                  : (swapsEnabled) => update({ swapsEnabled })
              }
            />
          </div>
          <IconButton
            variant="text"
            color="neutral"
            icon={open ? faChevronUp : faChevronDown}
            testId="swap-section-chevron"
            onClick={() => setOpen((v) => !v)}
          />
        </div>
      </div>
      {open && !config.swapsEnabled && (
        <div
          className={styles.offPlaceholder}
          data-testid="swap-section-off-placeholder"
        >
          <FontAwesomeIcon icon={faCircleInfo} fontSize={24} />
          <Typography variant="body" size="large" fontWeight="normal">
            Modifications are off for this exercise, including any you've added.
          </Typography>
        </div>
      )}
      {open && config.swapsEnabled && (
        <>
          <CatalogSearch
            search={search}
            atCap={config.proposals.length >= MAX_SWAP_PROPOSALS}
            disabled={p.disabled}
            onAdd={addProposal}
          />
          {search.state.type === 'idle' && config.proposals.length === 0 && (
            <div className={styles.emptyState}>
              <FontAwesomeIcon icon={faMagnifyingGlass} />
              <Typography>No modifications yet.</Typography>
              <Typography>Use search to find some.</Typography>
            </div>
          )}
          <hr className={styles.searchDivider} />
          <ModificationsList
            proposals={config.proposals}
            disabled={p.disabled}
            onEdit={(proposal) =>
              setEditedNameKey(exerciseNameKey(proposal.name))
            }
            onRemove={(removed) =>
              update({
                proposals: config.proposals.filter(
                  (proposal) => proposal !== removed,
                ),
              })
            }
          />
          {editedProposal && (
            <ExerciseSettingsModal
              proposal={editedProposal}
              onClose={() => setEditedNameKey(null)}
              onSave={(sets) => {
                update({
                  proposals: config.proposals.map((proposal) =>
                    proposal === editedProposal
                      ? { ...proposal, sets }
                      : proposal,
                  ),
                });
                setEditedNameKey(null);
              }}
            />
          )}
        </>
      )}
    </>
  );
};
